import { type ChangeEvent, type FC, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEmailTemplatesListQuery } from "@/partner/entities/marketing";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { interpolationFormat } from "@/partner/shared/lib";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { EmailTemplatesListToolbar } from "./EmailTemplatesListToolbar";

interface EmailTemplatesListProps {
  onEdit: (id: string) => void;
}

/**
 * Список писем: Card + MUI Table, выделение строк, пагинация, как
 * PollsLibraryList. Строка и название открывают диалог письма.
 */
export const EmailTemplatesList: FC<EmailTemplatesListProps> = ({ onEdit }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data, loading } = useEmailTemplatesListQuery({
    variables: { search: search || undefined, page, rowsPerPage },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.emailTemplates.nodes ?? [];
  const count = data?.emailTemplates.count ?? 0;
  const selectedIds = nodes.map((n) => n.id).filter((id) => selected[id]);
  const numSelected = selectedIds.length;
  const allSelected = numSelected > 0 && numSelected === nodes.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;

  const onSelectAll = (event: ChangeEvent<HTMLInputElement>) =>
    setSelected(
      event.target.checked
        ? Object.fromEntries(nodes.map((item) => [item.id, true]))
        : {}
    );

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return <EmptyListCard title={t("emailTemplates.list.empty")} />;
  }

  return (
    <Card elevation={0}>
      {numSelected > 0 && (
        <EmailTemplatesListToolbar
          indeterminate={indeterminate}
          allSelected={allSelected}
          numSelected={numSelected}
          onSelectAllClick={onSelectAll}
          selectedIds={selectedIds}
          onCloseSelected={() => setSelected({})}
        />
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={indeterminate}
                checked={allSelected}
                onChange={onSelectAll}
              />
            </TableCell>
            <TableCell>{t("emailTemplates.list.name")}</TableCell>
            {!isMobile && (
              <TableCell>{t("emailTemplates.list.languages")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("emailTemplates.list.updated")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((item) => (
            <TableRow
              key={item.id}
              hover
              selected={Boolean(selected[item.id])}
              onClick={() => onEdit(item.id)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell
                padding="checkbox"
                onClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  color="default"
                  checked={Boolean(selected[item.id])}
                  onChange={(event) =>
                    setSelected({
                      ...selected,
                      [item.id]: event.target.checked,
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <ItemTitleLink
                  to={`${PagePaths.emailTemplates}?tab=edit&id=${item.id}`}
                  onOpen={() => onEdit(item.id)}
                  color="text.primary"
                  underline="none"
                >
                  {item.name}
                </ItemTitleLink>
              </TableCell>
              {!isMobile && (
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {item.variants.map((variant) => (
                      <Chip
                        key={variant.language}
                        size="small"
                        label={variant.language.toUpperCase()}
                      />
                    ))}
                  </Stack>
                </TableCell>
              )}
              {!isMobile && (
                <TableCell align="right">
                  {interpolationFormat(item.updatedAt, "short", i18n.language)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
